import { NestedPartPayload } from '../../../services/api/loadingPlansService';
import { fromMatrix4, toMatrix4 } from './editMode.utils';

export interface RenestNestedPart {
  part_id: string;
  part_index?: number;
  order_number?: number;
  assigned_loading_layer?: number;
  orientation_index?: number;
  transform_matrix?: number[][];
  child_items?: RenestNestedPart[];
}

// Number of parts requested per windowed nesting batch.
export const DEFAULT_NESTING_BATCH_SIZE = Number(process.env.REACT_APP_NESTING_BATCH_SIZE) || 3;

/** Returns catalog part_ids not yet placed or excluded. */
export function getUndecidedPartIds(
  includedPartIds: string[],
  decidedPartIds: Set<string> | string[],
): string[] {
  const decided = decidedPartIds instanceof Set ? decidedPartIds : new Set(decidedPartIds);
  return includedPartIds.filter((partId) => !decided.has(partId) && !isDunnagePartId(partId));
}

/** Set of part IDs already placed, stacked on a pallet, or excluded. */
export function buildDecidedPartIds(
  payload: { part_id: string }[],
  rawNestedParts: RenestNestedPart[],
  excludedParts: { part_id: string }[],
): Set<string> {
  const rawById = new Map(rawNestedParts.map((part) => [part.part_id, part]));
  const decided = new Set<string>();
  for (const part of payload) {
    decided.add(part.part_id);
    for (const child of rawById.get(part.part_id)?.child_items ?? []) {
      if (child.part_id) decided.add(child.part_id);
    }
  }
  for (const excluded of excludedParts) {
    decided.add(excluded.part_id);
  }
  return decided;
}

export const IDENTITY_MATRIX: number[][] = [
  [1, 0, 0, 0],
  [0, 1, 0, 0],
  [0, 0, 1, 0],
  [0, 0, 0, 1],
];

export const getBasePartId = (partId: string): string =>
  partId.includes('#') ? partId.split('#')[0] : partId;

export const isDunnagePartId = (partId: string): boolean => getBasePartId(partId) === 'dunnage';
export const isPalletPartId = (partId: string): boolean => getBasePartId(partId) === 'pallet';
export const isSupportPartId = (partId: string): boolean => {
  const base = getBasePartId(partId);
  return base === 'dunnage' || base === 'pallet';
};

/**
 * World matrix of a part stacked on a pallet. The engine nests a pallet as one part with its children fixed
 * inside it, so a child's transform_matrix is relative to the pallet; preplaced parts need world matrices.
 */
export function toWorldChildMatrix(parentMatrix?: number[][], childMatrix?: number[][]): number[][] {
  if (!isMatrix4(parentMatrix)) return childMatrix ?? IDENTITY_MATRIX;
  if (!childMatrix) return parentMatrix;
  if (!isMatrix4(childMatrix)) return childMatrix;
  return fromMatrix4(toMatrix4(parentMatrix).multiply(toMatrix4(childMatrix)));
}

const isMatrix4 = (matrix?: number[][]): matrix is number[][] =>
  Array.isArray(matrix) && matrix.length === 4 && matrix.every((row) => Array.isArray(row) && row.length === 4);

// A pallet child with its world matrix, for when its scene object (which has it) is not loaded.
const withWorldMatrix = (child: RenestNestedPart, pallet: RenestNestedPart): RenestNestedPart => ({
  ...child,
  transform_matrix: toWorldChildMatrix(pallet.transform_matrix, child.transform_matrix),
});

/**
 * Flattens a list of nested parts, extracting child parts from pallets (with world matrices) while
 * excluding support containers (pallets and dunnage).
 */
export function flattenPlacableNestedParts(parts: RenestNestedPart[]): RenestNestedPart[] {
  const result: RenestNestedPart[] = [];

  for (const part of parts) {
    if (part.child_items && part.child_items.length > 0) {
      for (const child of part.child_items) {
        if (child.part_id && !isSupportPartId(child.part_id)) {
          result.push({
            part_id: child.part_id,
            part_index: child.part_index,
            assigned_loading_layer: child.assigned_loading_layer ?? part.assigned_loading_layer ?? 1,
            order_number: child.order_number ?? part.order_number ?? 0,
            orientation_index: child.orientation_index,
            transform_matrix: toWorldChildMatrix(part.transform_matrix, child.transform_matrix),
          });
        }
      }
    } else if (part.part_id && !isSupportPartId(part.part_id)) {
      result.push({
        part_id: part.part_id,
        part_index: part.part_index,
        assigned_loading_layer: part.assigned_loading_layer ?? 1,
        order_number: part.order_number ?? 0,
        orientation_index: part.orientation_index,
        transform_matrix: part.transform_matrix ?? IDENTITY_MATRIX,
      });
    }
  }

  return result;
}

/** Sorts nested parts by loading layer, then by order number. */
export function sortNestedParts<T extends RenestNestedPart>(parts: T[]): T[] {
  return [...parts].sort((a, b) => {
    const layerA = a.assigned_loading_layer ?? 1;
    const layerB = b.assigned_loading_layer ?? 1;
    if (layerA !== layerB) return layerA - layerB;
    return (a.order_number ?? 0) - (b.order_number ?? 0);
  });
}

export interface LayerPartItem {
  part_id: string;
  order_number?: number;
  orientation_index?: number;
  transform_matrix?: number[][];
  [key: string]: any;
}

export interface LayerDataStructure {
  supportObjects?: LayerPartItem[];
  parts?: LayerPartItem[];
}

export interface BuildPartialRenestParams {
  rawNestedParts: RenestNestedPart[];
  layerData: { [key: number]: LayerDataStructure };
  currentLayer: number;
  editedPartId: string;
  newTransformMatrix: number[][];
  replacedOldPartId?: string | null;
  scenePartsMap?: { [partId: string]: any };
  editedOrientationIndex?: number;
}

function resolveMatrix(
  rawPart: RenestNestedPart,
  scenePartsMap?: { [partId: string]: any },
  overrideMatrix?: number[][],
): number[][] {
  if (overrideMatrix) return overrideMatrix;
  const sceneObj = scenePartsMap?.[rawPart.part_id];
  return sceneObj?.matrix ? fromMatrix4(sceneObj.matrix) : (rawPart.transform_matrix ?? IDENTITY_MATRIX);
}

/**
 * Builds the Partial Re-Nesting payload: includes support objects and parts up to the edited slot,
 * omitting subsequent parts so the server recalculates the remaining layout.
 */
export function buildPartialRenestPayload(params: BuildPartialRenestParams): NestedPartPayload[] {
  const {
    rawNestedParts,
    layerData,
    currentLayer,
    editedPartId,
    newTransformMatrix,
    replacedOldPartId,
    scenePartsMap,
    editedOrientationIndex,
  } = params;

  if (!rawNestedParts?.length) return [];

  const currentLayerParts = layerData[currentLayer]?.parts || [];
  const targetSlotId = replacedOldPartId || editedPartId;
  // Slot first: a replacement that sits earlier in the layer must not move the cut-off point up.
  let editedIndexInLayer = currentLayerParts.findIndex(
    (p) => p.part_id === targetSlotId || p.child_items?.some((c: any) => c.part_id === targetSlotId)
  );
  if (editedIndexInLayer === -1) {
    editedIndexInLayer = currentLayerParts.findIndex(
      (p) => p.part_id === editedPartId || p.child_items?.some((c: any) => c.part_id === editedPartId)
    );
  }

  if (editedIndexInLayer === -1) {
    console.warn(`Target part ${targetSlotId} not found in current layer ${currentLayer}`);
    return [];
  }

  const payload: NestedPartPayload[] = [];
  const includedPartIds = new Set<string>();

  const ownIndices = (entry: RenestNestedPart) => ({
    orientationIndex: entry.orientation_index,
    partIndex: entry.part_index,
  });
  // A replacement is another part - another engine catalog entry, another set of orientations - so the indices of
  // the slot it takes must not be carried over to it. Without a part_index the backend resolves it by part id.
  const isReplacement = !!replacedOldPartId && replacedOldPartId !== editedPartId;
  const slotIndices = (slot: RenestNestedPart) => ({
    orientationIndex: editedOrientationIndex ?? (isReplacement ? undefined : slot.orientation_index),
    partIndex: isReplacement ? undefined : slot.part_index,
  });

  const appendToPayload = (
    partId: string,
    layer: number,
    matrix: number[][],
    orderNumber?: number,
    isSupport = false,
    orientationIndex?: number,
    partIndex?: number,
  ) => {
    if (!isSupport) {
      if (includedPartIds.has(partId)) return;
      includedPartIds.add(partId);
    }
    payload.push({
      part_id: partId,
      order_number: orderNumber ?? payload.length + 1,
      assigned_loading_layer: layer,
      orientation_index: orientationIndex,
      part_index: partIndex,
      transform_matrix: matrix,
    });
  };

  for (const raw of rawNestedParts) {
    const rawLayer = raw.assigned_loading_layer ?? 1;
    const isPallet = isPalletPartId(raw.part_id) || (raw.child_items && raw.child_items.length > 0);
    if (isPallet) {
      const children = raw.child_items || [];
      if (rawLayer < currentLayer) {
        for (const child of children) {
          if (!child.part_id || isSupportPartId(child.part_id)) continue;
          if (replacedOldPartId && child.part_id === editedPartId) continue;
          const isChildSlotTarget = child.part_id === targetSlotId;
          const childPartId = isChildSlotTarget ? editedPartId : child.part_id;
          const matrix = resolveMatrix(withWorldMatrix(child, raw), scenePartsMap, isChildSlotTarget ? newTransformMatrix : undefined);
          const indices = isChildSlotTarget ? slotIndices(child) : ownIndices(child);
          appendToPayload(childPartId, child.assigned_loading_layer ?? rawLayer, matrix, child.order_number ?? raw.order_number, false, indices.orientationIndex, indices.partIndex);
        }
        continue;
      }

      if (rawLayer === currentLayer) {
        const indexInLayer = currentLayerParts.findIndex((p) => p.part_id === raw.part_id);
        if (indexInLayer !== -1 && indexInLayer <= editedIndexInLayer) {
          for (const child of children) {
            if (!child.part_id || isSupportPartId(child.part_id)) continue;
            if (replacedOldPartId && child.part_id === editedPartId) continue;
            const isChildSlotTarget = child.part_id === targetSlotId;
            const childPartId = isChildSlotTarget ? editedPartId : child.part_id;
            const matrix = resolveMatrix(withWorldMatrix(child, raw), scenePartsMap, isChildSlotTarget ? newTransformMatrix : undefined);
            const indices = isChildSlotTarget ? slotIndices(child) : ownIndices(child);
            appendToPayload(childPartId, child.assigned_loading_layer ?? rawLayer, matrix, child.order_number ?? raw.order_number, false, indices.orientationIndex, indices.partIndex);
          }
        }
        continue;
      }

      continue;
    }

    const isSupport = isDunnagePartId(raw.part_id);
    const isSlotTarget = raw.part_id === targetSlotId;
    const isReplacementSource = raw.part_id === editedPartId;

    // 1. Earlier layers: keep all supports and placed parts (except moved replacement source)
    if (rawLayer < currentLayer) {
      if (replacedOldPartId && isReplacementSource) continue;

      const partId = isSlotTarget ? editedPartId : raw.part_id;
      const matrix = resolveMatrix(raw, scenePartsMap, isSlotTarget ? newTransformMatrix : undefined);
      const indices = isSlotTarget ? slotIndices(raw) : ownIndices(raw);
      appendToPayload(partId, rawLayer, matrix, raw.order_number, isSupport, indices.orientationIndex, indices.partIndex);
      continue;
    }

    // 2. Later layers: excluded for recalculation
    if (rawLayer > currentLayer) continue;

    // 3. Current layer:
    if (isSupport) {
      appendToPayload(raw.part_id, currentLayer, raw.transform_matrix ?? IDENTITY_MATRIX, raw.order_number, true, raw.orientation_index, raw.part_index);
      continue;
    }

    if (isSlotTarget) {
      const { orientationIndex, partIndex } = slotIndices(raw);
      appendToPayload(editedPartId, currentLayer, newTransformMatrix, raw.order_number, false, orientationIndex, partIndex);
      continue;
    }

    if (replacedOldPartId && isReplacementSource) continue;

    const indexInLayer = currentLayerParts.findIndex((p) => p.part_id === raw.part_id);
    if (indexInLayer === -1 || indexInLayer > editedIndexInLayer) continue;

    const matrix = resolveMatrix(raw, scenePartsMap);
    appendToPayload(raw.part_id, currentLayer, matrix, raw.order_number, false, raw.orientation_index, raw.part_index);
  }

  return payload;
}

export interface LockedLayerDataSwap {
  replacedOldPartId?: string | null;
  finalPartId: string;
  finalMatrix: number[][];
  finalOrientationIndex?: number;
}

/**
 * Keeps the items whose id is locked. Pallets are never part of the re-nest payload (only the
 * parts stacked on them are), so a pallet is kept - trimmed to its locked children - whenever at
 * least one of its children is locked; otherwise the whole stack would vanish from the plan.
 */
export function filterToLockedParts<T extends { part_id: string }>(items: T[], lockedPartIds: Set<string>): T[] {
  const kept: T[] = [];
  for (const item of items) {
    if (lockedPartIds.has(item.part_id)) {
      kept.push(item);
      continue;
    }
    const children: { part_id: string }[] | undefined = (item as any).child_items;
    const lockedChildren = children?.filter((child) => lockedPartIds.has(child.part_id));
    if (lockedChildren && lockedChildren.length > 0) {
      kept.push({ ...item, child_items: lockedChildren });
    }
  }
  return kept;
}

/** Drops `partId` from every pallet's child list (the part is now a top-level slot). */
export function removeChildFromPallets(
  palletChildrenMap: { [palletId: string]: string[] },
  partId: string,
): { [palletId: string]: string[] } {
  const result: { [palletId: string]: string[] } = {};
  Object.keys(palletChildrenMap).forEach((palletId) => {
    result[palletId] = palletChildrenMap[palletId].filter((childUuid) => childUuid !== partId);
  });
  return result;
}

/** Pallet -> children map restricted to the locked children, so navigation can't re-show the rest. */
export function buildLockedPalletChildrenMap(
  palletChildrenMap: { [palletId: string]: string[] },
  lockedPartIds: Set<string>,
  replacementPartId?: string | null,
): { [palletId: string]: string[] } {
  const result: { [palletId: string]: string[] } = {};
  Object.keys(palletChildrenMap).forEach((palletId) => {
    const children = palletChildrenMap[palletId].filter(
      (childUuid) => childUuid !== replacementPartId && lockedPartIds.has(childUuid),
    );
    if (children.length > 0) {
      result[palletId] = children;
    }
  });
  return result;
}

/**
 * Filters layerData down to the parts/support objects `lockedPartIds` keeps, applying the
 * edited/replaced part's swap first so its renamed entry (if any) is evaluated against the locked
 * set. Anything dropped is hidden in the scene so it disappears from the viewer immediately,
 * ahead of the server's recalculated layout coming back.
 */
export function buildLockedLayerData<T extends LayerPartItem>(
  layerData: { [key: number]: { parts: T[]; supportObjects: T[] } },
  lockedPartIds: Set<string>,
  scenePartsMap: { [partId: string]: any },
  palletChildrenMap: { [palletId: string]: string[] },
  setPalletChildrenVisibility: (
    partId: string,
    visible: boolean,
    scenePartsMap: { [partId: string]: any },
    palletChildrenMap: { [palletId: string]: string[] },
  ) => void,
  swap: LockedLayerDataSwap | null,
): { [key: number]: { parts: T[]; supportObjects: T[] } } {
  const truncated: { [key: number]: { parts: T[]; supportObjects: T[] } } = {};
  const replacedPartId = swap?.replacedOldPartId ? swap.finalPartId : null;
  // The replacement is the slot now, so toggling its old pallet must not touch it any more.
  const childrenMap = replacedPartId ? removeChildFromPallets(palletChildrenMap, replacedPartId) : palletChildrenMap;

  const hide = (partId: string) => {
    const sceneObj = scenePartsMap[partId];
    if (sceneObj) {
      sceneObj.visible = false;
      sceneObj.updateMatrixWorld(true);
    }
  };

  Object.keys(layerData).map(Number).forEach((layerIndex) => {
    const layer = layerData[layerIndex];

    const applySwap = (item: T): T => {
      let updatedItem = item;
      if (swap?.replacedOldPartId && item.part_id === swap.replacedOldPartId) {
        updatedItem = {
          ...item,
          part_id: swap.finalPartId,
          transform_matrix: swap.finalMatrix,
          orientation_index: swap.finalOrientationIndex ?? item.orientation_index,
        };
      } else if (swap && item.part_id === swap.finalPartId) {
        updatedItem = {
          ...item,
          transform_matrix: swap.finalMatrix,
          orientation_index: swap.finalOrientationIndex ?? item.orientation_index,
        };
      }

      if (swap && Array.isArray((updatedItem as any).child_items)) {
        const updatedChildren = (updatedItem as any).child_items.map((child: any) => {
          if (swap.replacedOldPartId && child.part_id === swap.replacedOldPartId) {
            return {
              ...child,
              part_id: swap.finalPartId,
              transform_matrix: swap.finalMatrix,
              orientation_index: swap.finalOrientationIndex ?? child.orientation_index,
            };
          }
          if (child.part_id === swap.finalPartId) {
            return {
              ...child,
              transform_matrix: swap.finalMatrix,
              orientation_index: swap.finalOrientationIndex ?? child.orientation_index,
            };
          }
          return child;
        });
        return { ...updatedItem, child_items: updatedChildren };
      }

      return updatedItem;
    };

    // The replacement's original entry (top-level or stacked on a pallet) is gone once it takes the slot.
    const swappedParts = layer.parts
      .filter((item) => item.part_id !== replacedPartId)
      .map((item) =>
        replacedPartId && Array.isArray(item.child_items)
          ? { ...item, child_items: item.child_items.filter((child: { part_id: string }) => child.part_id !== replacedPartId) }
          : item,
      )
      .map(applySwap);

    const keptParts = filterToLockedParts(swappedParts, lockedPartIds);
    const keptSupport = layer.supportObjects.filter((item) => lockedPartIds.has(item.part_id));
    const keptPartIds = new Set(keptParts.map((item) => item.part_id));

    swappedParts.forEach((item) => {
      if (keptPartIds.has(item.part_id)) {
        // A kept pallet may carry children that were not locked; they are re-nested by the server.
        (item.child_items as { part_id: string }[] | undefined)?.forEach((child) => {
          if (!lockedPartIds.has(child.part_id)) hide(child.part_id);
        });
        return;
      }
      hide(item.part_id);
      setPalletChildrenVisibility(item.part_id, false, scenePartsMap, childrenMap);
    });

    layer.supportObjects.forEach((item) => {
      if (lockedPartIds.has(item.part_id)) return;
      hide(item.part_id);
    });

    if (keptParts.length > 0 || keptSupport.length > 0) {
      truncated[layerIndex] = { parts: keptParts, supportObjects: keptSupport };
    }
  });

  return truncated;
}

/**
 * Updates layerData in-place for the edited/replaced part without dropping any downstream parts
 * or support objects (dunnages).
 */
export function buildUpdatedLayerData<T extends LayerPartItem>(
  layerData: { [key: number]: { parts: T[]; supportObjects: T[] } },
  swap: LockedLayerDataSwap | null,
): { [key: number]: { parts: T[]; supportObjects: T[] } } {
  if (!swap) return layerData;

  const result: { [key: number]: { parts: T[]; supportObjects: T[] } } = {};
  const replacedPartId = swap.replacedOldPartId ? swap.finalPartId : null;

  const applySwap = (item: T): T => {
    let updatedItem = item;
    if (swap.replacedOldPartId && item.part_id === swap.replacedOldPartId) {
      updatedItem = {
        ...item,
        part_id: swap.finalPartId,
        transform_matrix: swap.finalMatrix,
        orientation_index: swap.finalOrientationIndex ?? item.orientation_index,
      };
    } else if (item.part_id === swap.finalPartId) {
      updatedItem = {
        ...item,
        transform_matrix: swap.finalMatrix,
        orientation_index: swap.finalOrientationIndex ?? item.orientation_index,
      };
    }

    if (Array.isArray((updatedItem as any).child_items)) {
      const updatedChildren = (updatedItem as any).child_items.map((child: any) => {
        if (swap.replacedOldPartId && child.part_id === swap.replacedOldPartId) {
          return {
            ...child,
            part_id: swap.finalPartId,
            transform_matrix: swap.finalMatrix,
            orientation_index: swap.finalOrientationIndex ?? child.orientation_index,
          };
        }
        if (child.part_id === swap.finalPartId) {
          return {
            ...child,
            transform_matrix: swap.finalMatrix,
            orientation_index: swap.finalOrientationIndex ?? child.orientation_index,
          };
        }
        return child;
      });
      return { ...updatedItem, child_items: updatedChildren };
    }

    return updatedItem;
  };

  Object.keys(layerData).map(Number).forEach((layerIndex) => {
    const layer = layerData[layerIndex];
    if (!layer) return;

    // The replacement's original entry (top-level or stacked on a pallet) is gone once it takes the slot.
    const swappedParts = (layer.parts || [])
      .filter((item) => !replacedPartId || item.part_id !== replacedPartId || swap.replacedOldPartId === swap.finalPartId)
      .map((item) =>
        replacedPartId && Array.isArray(item.child_items)
          ? { ...item, child_items: item.child_items.filter((child: { part_id: string }) => child.part_id !== replacedPartId) }
          : item,
      )
      .map(applySwap);

    const swappedSupport = (layer.supportObjects || []).map(applySwap);

    result[layerIndex] = {
      parts: swappedParts,
      supportObjects: swappedSupport,
    };
  });

  return result;
}

